import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { getMapData, getMapStats, MapDataPoint, MapStats } from '../services/loginActivityService';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icon issue with webpack
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Configure default marker icon (blue - successful logins)
const defaultIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Red marker for failed logins (inline SVG DivIcon)
const failedIcon = new DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="25" height="41">
    <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 2.4.7 4.6 1.9 6.5L12.5 41l10.6-22C24.3 17.1 25 14.9 25 12.5 25 5.6 19.4 0 12.5 0z" fill="#DC2626" stroke="#991B1B" stroke-width="1"/>
    <circle cx="12.5" cy="12.5" r="5" fill="white"/>
  </svg>`,
  className: '',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

// Green marker for page visits
const pageVisitIcon = new DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="25" height="41">
    <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 2.4.7 4.6 1.9 6.5L12.5 41l10.6-22C24.3 17.1 25 14.9 25 12.5 25 5.6 19.4 0 12.5 0z" fill="#059669" stroke="#065F46" stroke-width="1"/>
    <circle cx="12.5" cy="12.5" r="5" fill="white"/>
  </svg>`,
  className: '',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

type TimeFilter = 'all' | '24h' | '1week' | '1month' | '3months';
type LoginFilter = 'successful' | 'failed' | 'page_visits' | 'all';

const UserLocationMap: React.FC = () => {
  const { token } = useAuth();

  const [mapData, setMapData] = useState<MapDataPoint[]>([]);
  const [stats, setStats] = useState<MapStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [loginFilter, setLoginFilter] = useState<LoginFilter>('successful');

  useEffect(() => {
    loadMapData();
  }, [token, timeFilter, loginFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMapData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      // Convert time filter to days parameter
      let days: number | undefined;
      switch (timeFilter) {
        case '24h':
          days = 1;
          break;
        case '1week':
          days = 7;
          break;
        case '1month':
          days = 30;
          break;
        case '3months':
          days = 90;
          break;
        case 'all':
        default:
          days = undefined; // No filter
          break;
      }

      // Convert login filter to successFilter and eventType parameters
      let successFilter: boolean | undefined;
      let eventTypeFilter: string | undefined;

      if (loginFilter === 'successful') {
        successFilter = true;
        eventTypeFilter = 'login';
      } else if (loginFilter === 'failed') {
        successFilter = false;
        eventTypeFilter = 'login';
      } else if (loginFilter === 'page_visits') {
        eventTypeFilter = 'page_visit';
      }
      // loginFilter === 'all' → no filters

      const [dataResponse, statsResponse] = await Promise.all([
        getMapData(token, days, successFilter, eventTypeFilter),
        getMapStats(token, days, successFilter, eventTypeFilter)
      ]);

      setMapData(dataResponse);
      setStats(statsResponse);
    } catch (err: any) {
      console.error('Error loading map data:', err);
      setError(err.response?.data?.error || 'Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFailureReason = (reason: string | null): string => {
    switch (reason) {
      case 'invalid_email':
        return 'Email not found';
      case 'invalid_password':
        return 'Wrong password';
      case 'account_inactive':
        return 'Account inactive';
      default:
        return reason || 'Unknown';
    }
  };

  const getMarkerIcon = (point: MapDataPoint) => {
    if (point.event_type === 'page_visit') return pageVisitIcon;
    return point.success ? defaultIcon : failedIcon;
  };

  const getStatsLabel = (): string => {
    switch (loginFilter) {
      case 'successful':
        return 'Successful Logins';
      case 'failed':
        return 'Failed Attempts';
      case 'page_visits':
        return 'Page Visits';
      case 'all':
        return 'All Activity';
    }
  };

  const getEmptyStateMessage = (): string => {
    switch (loginFilter) {
      case 'successful':
        return 'No successful login locations found for the selected time period.';
      case 'failed':
        return 'No failed login attempts with location data found for the selected time period.';
      case 'page_visits':
        return 'No page visit locations found for the selected time period.';
      case 'all':
        return 'No activity locations found for the selected time period.';
    }
  };

  const getTotalLabel = (): string => {
    switch (loginFilter) {
      case 'failed':
        return 'Failed Attempts';
      case 'page_visits':
        return 'Total Visits';
      case 'all':
        return 'Total Activity';
      default:
        return 'Total Locations';
    }
  };

  const getUniqueLabel = (): string => {
    switch (loginFilter) {
      case 'failed':
        return 'Unique Identities';
      case 'page_visits':
        return 'Unique Visitors';
      default:
        return 'Unique Users';
    }
  };

  const renderPopup = (point: MapDataPoint) => {
    if (point.event_type === 'page_visit') {
      return (
        <div className="p-2">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-gray-900">Anonymous Visitor</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
              Page Visit
            </span>
          </div>
          <div className="space-y-1 text-sm">
            <p><span className="font-medium">Location:</span> {point.city}, {point.region}, {point.country}</p>
            <p><span className="font-medium">Visit Time:</span> {formatDate(point.login_timestamp)}</p>
            {point.referrer && (
              <p><span className="font-medium">Referrer:</span> {point.referrer}</p>
            )}
            {point.user_agent && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-medium">Browser:</span> {point.user_agent.substring(0, 50)}...
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="p-2">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-semibold text-gray-900">{point.user_name}</h3>
          {point.success ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Success
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Failed
            </span>
          )}
        </div>
        <div className="space-y-1 text-sm">
          <p><span className="font-medium">Email:</span> {point.user_email}</p>
          <p><span className="font-medium">Company:</span> {point.company_name}</p>
          <p><span className="font-medium">Location:</span> {point.city}, {point.region}, {point.country}</p>
          <p><span className="font-medium">Login Time:</span> {formatDate(point.login_timestamp)}</p>
          {!point.success && point.failure_reason && (
            <p>
              <span className="font-medium text-red-600">Failure Reason:</span>{' '}
              <span className="text-red-600">{formatFailureReason(point.failure_reason)}</span>
            </p>
          )}
          {point.user_agent && (
            <p className="text-xs text-gray-500 mt-2">
              <span className="font-medium">Browser:</span> {point.user_agent.substring(0, 50)}...
            </p>
          )}
        </div>
      </div>
    );
  };

  const showLegend = loginFilter === 'all';

  return (
    <Layout>
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Location Map</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Visualize user login locations and page visits across all companies
          </p>
        </div>

        {/* Filters Row */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
          {/* Activity Type Filter */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Activity Type:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setLoginFilter('successful')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  loginFilter === 'successful'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Successful Logins
              </button>
              <button
                onClick={() => setLoginFilter('failed')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  loginFilter === 'failed'
                    ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Failed Logins
              </button>
              <button
                onClick={() => setLoginFilter('page_visits')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  loginFilter === 'page_visits'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Page Visits
              </button>
              <button
                onClick={() => setLoginFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  loginFilter === 'all'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
            </div>
          </div>

          {/* Time Period Filter */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTimeFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  timeFilter === 'all'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All Time
              </button>
              <button
                onClick={() => setTimeFilter('24h')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  timeFilter === '24h'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Last 24 Hours
              </button>
              <button
                onClick={() => setTimeFilter('1week')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  timeFilter === '1week'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Last Week
              </button>
              <button
                onClick={() => setTimeFilter('1month')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  timeFilter === '1month'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Last Month
              </button>
              <button
                onClick={() => setTimeFilter('3months')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  timeFilter === '3months'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Last 3 Months
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading map data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {!loading && stats && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${
                  loginFilter === 'failed'
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : loginFilter === 'page_visits'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30'
                    : 'bg-indigo-100 dark:bg-indigo-900/30'
                }`}>
                  <svg className={`h-6 w-6 ${
                    loginFilter === 'failed'
                      ? 'text-red-600 dark:text-red-400'
                      : loginFilter === 'page_visits'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-indigo-600 dark:text-indigo-400'
                  }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getTotalLabel()}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_logins_with_location}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Countries</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.unique_countries}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                  <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {getUniqueLabel()}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.unique_users}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                  <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Most Recent</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {stats.most_recent_login ? formatDate(stats.most_recent_login) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Map */}
        {!loading && mapData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getStatsLabel()} ({mapData.length} total)
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Click on any marker to view details
                {showLegend && (
                  <span className="ml-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1 align-middle"></span>
                    <span className="text-xs">Successful Login</span>
                    <span className="inline-block w-3 h-3 rounded-full bg-red-500 ml-3 mr-1 align-middle"></span>
                    <span className="text-xs">Failed Login</span>
                    <span className="inline-block w-3 h-3 rounded-full bg-emerald-600 ml-3 mr-1 align-middle"></span>
                    <span className="text-xs">Page Visit</span>
                  </span>
                )}
              </p>
            </div>
            <div className="h-[600px]">
              <MapContainer
                center={[20, 0]} // Center on world
                zoom={2}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapData.map((point) => (
                  <Marker
                    key={point.id}
                    position={[point.latitude, point.longitude]}
                    icon={getMarkerIcon(point)}
                  >
                    <Popup>
                      {renderPopup(point)}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && mapData.length === 0 && !error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No locations found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {getEmptyStateMessage()}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserLocationMap;
